import { useState, useEffect } from 'react';
import { Dropdown, Icon } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  GET_PDF_FORMAT,
  GET_EXCEL_FORMAT,
  GET_CSV_FORMAT,
  FileType,
} from '@behavior/reports/types';
import { entityType as et } from '../../../../../constants';

type Props = {
  fileType: FileType;
  selectedRows: any;
  actions: any;
  filterState: any;
  allFilters: any;
};

type DropdownItemReturnType = {
  icon: JSX.Element;
  labelClassName: string;
  translateValue: string;
  format: any;
};

const ExportDropdownItems = ({
  fileType,
  selectedRows,
  actions,
  filterState,
  allFilters,
}: Props) => {
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (
      isExportingAll &&
      allFilters[et.inventoryAllForExport] !== undefined &&
      allFilters[et.inventoryAllForExport].filteredList !== undefined &&
      allFilters[et.inventoryAllForExport].filteredList.result.length > 0
    ) {
      const exportIds = allFilters.inventoryAllForExport.filteredList.result.map((o: any) => o.ID);
      if (exportIds.length > 0) {
        actions.getInventoryReport(
          exportIds,
          selectedExportFormat,
          allFilters,
          filterState,
          (i18n as any).language
        );
      }
      setIsExportingAll(false);
    }
  });

  const dropdownItem = getDropdownItemByType(fileType);

  const onExportSelected = () => {
    const exportIds = Object.keys(selectedRows);
    if (exportIds.length > 0) {
      actions.getInventoryReport(
        exportIds,
        dropdownItem?.format,
        allFilters,
        filterState,
        (i18n as any).language
      );
    } else {
      (toast as any).warning(t('message.title.warning.export.no.selected.rows'));
    }
  };

  const onExportAll = () => {
    const filterStateCopy = JSON.parse(JSON.stringify(filterState));
    filterStateCopy.dataViewId = 0;
    filterStateCopy.pageSize = 999999;

    actions.applyFilterAction(
      'inventoryAllForExport',
      filterStateCopy,
      filterStateCopy.filterValues
    );
    setIsExportingAll(true);
    setSelectedExportFormat((dropdownItem as any).format);
  };

  return dropdownItem ? (
    <>
      <Dropdown.Item
        onClick={() => {
          onExportSelected();
        }}
      >
        {dropdownItem.icon}
        <label className={dropdownItem.labelClassName}>
          {t('inventory.overview.page.view.options.container.export.option', {
            value: dropdownItem.translateValue,
          })}
        </label>
      </Dropdown.Item>
      <Dropdown.Item
        onClick={() => {
          onExportAll();
        }}
      >
        {dropdownItem.icon}
        <label className={dropdownItem.labelClassName}>
          {t('inventory.overview.page.view.options.container.export.all.option', {
            value: dropdownItem.translateValue,
          })}
        </label>
      </Dropdown.Item>
    </>
  ) : (
    <></>
  );
};

const getDropdownItemByType = (fileType: FileType): DropdownItemReturnType | undefined => {
  const differentExports: Record<FileType, DropdownItemReturnType> = {
    [FileType.PDF]: {
      icon: <Icon name="file pdf" color="red" />,
      labelClassName: 'redTextLabel',
      translateValue: FileType.PDF,
      format: GET_PDF_FORMAT,
    },
    [FileType.EXCEL]: {
      icon: <Icon name="file excel" color="green" />,
      labelClassName: 'greenTextLabel',
      translateValue: FileType.EXCEL,
      format: GET_EXCEL_FORMAT,
    },
    [FileType.CSV]: {
      icon: <Icon name="file csv" color="purple" />,
      labelClassName: 'purpleTextLabel',
      translateValue: FileType.CSV,
      format: GET_CSV_FORMAT,
    },
  };

  const attributes = differentExports[fileType];

  if (!attributes) {
    console.error(
      `Invalid value for fileType: should be only ${Object.values(FileType).join(', ')} but found:`,
      fileType
    );
  }

  return attributes;
};

export default ExportDropdownItems;
